import * as React from 'react';
import {
  fetchApi,
  GCacheQuerySectionProps,
  makeResetCache,
  PaginatorAndListSettings,
  ParallelLoad,
  selectUser,
  UneditableReference,
  useCacheQuery,
  useCacheRecord,
} from 'gossamer-front-end';
import { ConvoMessage, ConvoMessageRow, ConvoUserView, UserSimpleView } from 'cb-model';
import { displayLocaleDate, displayLocaleDateTime, Filter, getFilter, getRowKey, QueryOptions } from 'gossamer-unified';
import styles from './InboxPage.module.css';
import { NewMessage } from './NewMessage';
import { useDispatch, useSelector } from 'react-redux';
import { getConvoContextInfo, getRelevantUserId } from 'cb-unified';

interface RightPaneProps {
  convoId: number;
}

const UNREAD_CLEAR_TIMEOUT = 2000;

export const RightPane = (props: RightPaneProps) => {
  const user = useSelector(selectUser);
  const [messageQueryOptions, setMessageQueryOptions] = React.useState({
    where: { and: [getFilter('convo_id', 'eq', props.convoId)] },
    order: [['created_at', 'DESC']],
    limit: 15,
  } as QueryOptions);

  const convoRecord = useCacheRecord(ConvoUserView, String(props.convoId));
  const messageQuery = useCacheQuery(ConvoMessage, messageQueryOptions);
  const dispatch = useDispatch();

  React.useEffect(() => {
    const newQueryOptions = { ...messageQueryOptions };
    const filter = newQueryOptions?.where?.and?.find((filter: Filter) => filter.fieldId === 'convo_id') as Filter;
    if (filter) {
      filter.value = props.convoId;
    }
    setMessageQueryOptions(newQueryOptions);
  }, [props.convoId]);

  React.useEffect(() => {
    if (convoRecord.data && convoRecord.data.latest_read_message_id < convoRecord.data.latest_message_id) {
      const timeout = setTimeout(async () => {
        await fetchApi('POST', '/main/set-convo-read-mark', {
          convoId: props.convoId,
        });
        const resetCache = makeResetCache(dispatch);
        resetCache('ConvoUserView', String(props.convoId));
      }, UNREAD_CLEAR_TIMEOUT);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [convoRecord.data]);

  const contextInfo =
    convoRecord.data &&
    getConvoContextInfo(
      convoRecord.data.forum_type,
      convoRecord.data.forum_key,
      getRelevantUserId(convoRecord.data.created_by, convoRecord.data.recipient_user_id, user?.userId),
    );
  return (
    <>
      <div className={styles.ConvoHeader}>
        <h2>{convoRecord.data?.subject_text || ''}</h2>
        <div>
          {convoRecord.data && (
            <>
              <img src={contextInfo.icon} />
              {/* prevent navigation to admin pages for regular users */}
              <UneditableReference
                refEntity={contextInfo.entity}
                value={contextInfo.key}
                suppressLink={user?.roleId === 'user'}
              />
            </>
          )}
        </div>
      </div>
      <PaginatorAndListSettings
        entity={ConvoMessage}
        gCacheQuery={messageQuery}
        queryOptions={messageQueryOptions}
        render={(innerProps) => (
          <MessageList
            {...innerProps}
            latestReadMessageId={convoRecord.data?.latest_read_message_id || null}
            currentUserId={user?.userId}
          />
        )}
        setQueryOptions={setMessageQueryOptions}
        showSettingsControl={false}
        showItemCount={false}
      />
      <NewMessage convoId={props.convoId} />
    </>
  );
};

interface MessageListProps extends GCacheQuerySectionProps<ConvoMessageRow> {
  currentUserId: string;
  latestReadMessageId: number | null;
}

const MessageList = (props: MessageListProps) => {
  const dataCopy = props.gCacheQuery.data ? [...props.gCacheQuery.data] : [];
  const children: React.JSX.Element[] = [];
  let prevSender: string | null = null;
  let prevDate: string | null = null;
  dataCopy.reverse().forEach((row) => {
    const thisDate = displayLocaleDate(new Date(row.created_at));
    if (thisDate !== prevDate) {
      children.push(
        <div className={styles.DayBreak} key={thisDate}>
          {thisDate}
        </div>,
      );
    }
    children.push(
      <MessageTile
        key={getRowKey(ConvoMessage, row)}
        row={row}
        currentUserId={props.currentUserId}
        latestReadMessageId={props.latestReadMessageId}
        includeSenderName={row.created_by !== prevSender && row.created_by !== props.currentUserId}
      />,
    );
    prevSender = row.created_by;
    prevDate = thisDate;
  });
  return (
    <ParallelLoad responses={[props.gCacheQuery]}>
      <div className={styles.MessageList}>{children}</div>
    </ParallelLoad>
  );
};

interface MessageTileProps {
  currentUserId: string;
  latestReadMessageId: number | null;
  row: ConvoMessageRow;
  includeSenderName: boolean;
}

const MessageTile = (props: MessageTileProps) => {
  const isFromCurrentUser = props.currentUserId === props.row.created_by;
  const classes = [styles.MessageTile];
  if (isFromCurrentUser) {
    classes.push(styles.FromCurrentUser);
  }
  if (props.row.id > props.latestReadMessageId) {
    classes.push(styles.Unread);
  }
  return (
    <div className={classes.join(' ')}>
      <div>
        {props.includeSenderName && (
          <div>
            <UneditableReference refEntity={UserSimpleView} value={props.row.created_by} />
          </div>
        )}
        <span>{props.row.message_text}</span>
        <div className={styles.VeryMuted}>
          {displayLocaleDateTime(new Date(props.row.created_at)).substring(10, 15)}
        </div>
      </div>
    </div>
  );
};
